import { STUDIO_MODES } from '../studio/data/studioCatalog'
import type { AspectRatio, StudioMode } from '../studio/domain/studioTypes'
import { VOXNELA_50_STYLES } from './voxnela50Styles'
import {
  CHART_CREATION_STYLES,
  IMAGE_CREATION_STYLES,
  POSTER_CREATION_STYLES,
  type VisualCreationStyle,
} from './visualCreationStyles'
import { getVisualCreationStyleDescription } from './visualCreationStyleDescriptions'
import {
  VOXNELA_TASK_CATEGORIES,
  getTasksByCategory,
  type VoxnelaTaskCategory,
  type VoxnelaTaskItem,
} from './voxnela56Tasks'
import { buildStyleSearchTags } from './styleSearch'
import { taskCategoryLabels } from '../i18n/catalog'
import { supportedLocales } from '../types/simulation'

const MODE_CATEGORY: Record<StudioMode, VoxnelaTaskCategory> = {
  style_transfer: 'stylize',
  virtual_tryon: 'stylize',
  magazine_cover: 'editorial',
  poster_studio: 'poster',
  infographic: 'business',
  time_warp: 'time_traveler',
  commercial: 'product',
  outpaint_relight: 'stylize',
  portrait_retouch: 'stylize',
  artistic_collage: 'creative',
}

const MODE_ASPECT_RATIO: Record<StudioMode, AspectRatio> = {
  style_transfer: '4:5',
  virtual_tryon: '4:5',
  magazine_cover: '4:5',
  poster_studio: '4:5',
  infographic: '16:9',
  time_warp: '16:9',
  commercial: '16:9',
  outpaint_relight: '16:9',
  portrait_retouch: '4:5',
  artistic_collage: '4:5',
}

const CATEGORY_SYMBOL: Record<VoxnelaTaskCategory, string> = {
  voxnela: '◢',
  business: '▥',
  product: '◉',
  stylize: '◇',
  creative: '◌',
  time_traveler: '◷',
  poster: '▣',
  editorial: '▤',
}

const DEFAULT_SUGGESTIONS: [string, string] = [
  'Kaynak görselini yükle veya oluşturulacak sahneyi tarif et.',
  'Korunacak ayrıntıları, görünür metni ve kullanım amacını belirt.',
]

const CREATIVE_IMAGE_STYLE_IDS = new Set([
  'cute-3d-miniature',
  'anime',
  'surreal-reality-warp',
  'retrofuturism',
  'bold-minimalism',
  'dark-fantasy',
  'claymation',
  'y2k-cyber-pop',
  'neo-brutalism',
  'graphic-novel',
])

const RETIRED_INFOGRAPHIC_STYLE_IDS = new Set([
  'vox_explainer_diagram',
  'chronological_timeline',
  'isometric_blueprint',
  'comparison_matrix',
])

function importedStyleCategory(style: VisualCreationStyle): VoxnelaTaskCategory {
  if (style.kind === 'poster') return 'poster'
  if (style.kind === 'chart') return 'business'
  return CREATIVE_IMAGE_STYLE_IDS.has(style.id) ? 'creative' : 'stylize'
}

function importedStyleMode(style: VisualCreationStyle): StudioMode {
  if (style.kind === 'poster') return 'poster_studio'
  if (style.kind === 'chart') return 'infographic'
  return 'style_transfer'
}

function importedStyleBadge(style: VisualCreationStyle): string {
  if (style.kind === 'poster') return 'Afiş Stili'
  if (style.kind === 'chart') return 'Grafik Stili'
  return 'Görsel Stili'
}

function importedStyleSuggestions(style: VisualCreationStyle): [string, string] {
  if (style.kind === 'poster') {
    return [
      'Başlık, tarih, yer ve görünmesini istediğin diğer metinleri yaz.',
      'Varsa kişi, ürün, logo veya ana görselini yükle.',
    ]
  }
  if (style.kind === 'chart') {
    return [
      'Veriyi, tabloyu veya kaynak belgeni yükle; değerler aynen korunur.',
      'Vurgulanacak karşılaştırmayı, dönemi ve hedef okuyucuyu belirt.',
    ]
  }
  return [
    'Kendi görselini yükle veya oluşturmak istediğin sahneyi tarif et.',
    'Korunacak kişi, nesne, renk ve kompozisyon ayrıntılarını belirt.',
  ]
}

function createImportedVisualStyleTasks(): VoxnelaTaskItem[] {
  return [
    ...IMAGE_CREATION_STYLES,
    ...POSTER_CREATION_STYLES,
    ...CHART_CREATION_STYLES,
  ].filter((style) => style.id !== 'handmade-collage').map((style) => {
    const category = importedStyleCategory(style)
    const title = style.labels.tr || style.labels.en
    return {
      id: `visual_style_${style.kind}_${style.id}`,
      category,
      title,
      shortTitle: title,
      emoji: CATEGORY_SYMBOL[category],
      badge: importedStyleBadge(style),
      description: getVisualCreationStyleDescription(style, 'tr'),
      suggestions: importedStyleSuggestions(style),
      sampleImageUrl: style.samplePath,
      aspectRatio: '9:16',
      recommendedStyleId: style.id,
      studioMode: importedStyleMode(style),
      promptTemplate: style.promptContract,
      searchTags: [
        ...Object.values(style.labels),
        style.kind,
        style.id.replaceAll('-', ' '),
      ],
    }
  })
}

// These legacy trend entries only have SVG-like placeholder previews (empty
// gradients, radar rings, wireframes or large X marks). They are not usable
// production styles and must never appear in the shared Home/Explore catalog.
export const NON_PRODUCTION_STYLE_IDS = new Set([
  'anime-cinematic',
  'clay-soft-3d',
  'data-core-infographic',
  'hand-drawn-doodle',
  'heritage-remix',
  'high-fashion-surreal',
  'hyperreal-product-cgi',
  'miniature-diorama',
  'naive-folk',
  'painterly-anime',
  'papercut-layered',
  'pixel-retro-tech',
  'textile-fiber-art',
  'ui-core',
])

function normalizeStyleId(value: string): string {
  return value.toLocaleLowerCase('en-US').replace(/[^a-z0-9]/g, '')
}

function createStudioModeTasks(): VoxnelaTaskItem[] {
  return STUDIO_MODES.flatMap((mode) => {
    const category = MODE_CATEGORY[mode.id]
    return mode.subcategories.filter((style) => !RETIRED_INFOGRAPHIC_STYLE_IDS.has(style.id)).map((style) => ({
      id: style.id,
      category,
      title: style.label,
      shortTitle: style.label,
      emoji: CATEGORY_SYMBOL[category],
      badge: style.badge || mode.shortLabel,
      description: style.description || style.subtitle,
      suggestions: DEFAULT_SUGGESTIONS,
      sampleImageUrl: style.imageUrl,
      aspectRatio: MODE_ASPECT_RATIO[mode.id],
      recommendedStyleId: style.id,
      studioMode: style.mode,
      promptTemplate: style.realPrompt || style.samplePrompt,
      searchTags: style.tags,
    }))
  })
}

function createVoxnelaTrendTasks(): VoxnelaTaskItem[] {
  return VOXNELA_50_STYLES
    .filter((style) => !NON_PRODUCTION_STYLE_IDS.has(style.id))
    .map((style) => ({
    id: style.id,
    category: 'stylize',
    title: style.title,
    shortTitle: style.title,
    emoji: CATEGORY_SYMBOL.stylize,
    badge: style.badge,
    description: style.description,
    suggestions: style.suggestions || DEFAULT_SUGGESTIONS,
    sampleImageUrl: style.imageUrl,
    aspectRatio: style.aspectRatio,
    recommendedStyleId: style.id,
    studioMode: 'style_transfer',
    promptTemplate: style.realPrompt,
    }))
}

export const IMPORTED_VISUAL_STYLE_TASKS = createImportedVisualStyleTasks()

const IMPORTED_STYLES = [
  ...createStudioModeTasks(),
  ...createVoxnelaTrendTasks(),
  ...IMPORTED_VISUAL_STYLE_TASKS,
]

export const APPLICATION_STYLES_BY_CATEGORY: Record<VoxnelaTaskCategory, VoxnelaTaskItem[]> =
  Object.fromEntries(
    VOXNELA_TASK_CATEGORIES.map((category) => {
      const styles = [
        ...getTasksByCategory(category.id).map((task) => ({ ...task, category: category.id })),
        ...IMPORTED_STYLES.filter((task) => task.category === category.id),
      ]
      const uniqueStyles = new Map<string, VoxnelaTaskItem>()
      styles.forEach((style) => {
        const key = normalizeStyleId(style.id)
        if (!uniqueStyles.has(key)) uniqueStyles.set(key, style)
      })
      return [
        category.id,
        Array.from(uniqueStyles.values()).map((style) => ({
          ...style,
          searchTags: buildStyleSearchTags(style, category.label).concat(
            supportedLocales.map((locale) => taskCategoryLabels[locale][category.id]),
          ),
        })),
      ]
    }),
  ) as Record<VoxnelaTaskCategory, VoxnelaTaskItem[]>

export function getApplicationStylesByCategory(
  category: VoxnelaTaskCategory,
): VoxnelaTaskItem[] {
  return APPLICATION_STYLES_BY_CATEGORY[category]
}

export const ALL_APPLICATION_STYLES = VOXNELA_TASK_CATEGORIES.flatMap(
  (category) => APPLICATION_STYLES_BY_CATEGORY[category.id],
)
